import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { UserHostDetails } from '@/infra/sse/shared/payloads'
import { WizardModal } from '@/common/components'
import type { HostStartSessionWizardStep } from '../types'
import { useHostProjects } from '../api'
import { useStartSessionForm } from '../hooks/useStartSessionForm'
import { ProjectSelectStep } from './steps/ProjectSelectStep'
import { SessionNameStep } from './steps/SessionNameStep'
import { InitialMessageStep } from './steps/InitialMessageStep'
import { PermissionsModeStep } from './steps/PermissionsModeStep'
import { ReviewStep } from './steps/ReviewStep'

interface HostStartSessionModalProps {
  readonly hostname: string
  readonly hostId: string
  readonly userHostDetails: UserHostDetails
  readonly onClose: () => void
}

export const HostStartSessionModal: FC<HostStartSessionModalProps> = ({
  hostname,
  hostId,
  userHostDetails,
  onClose,
}) => {
  const [step, setStep] = useState<HostStartSessionWizardStep>('project_select')
  const [selectedProject, setSelectedProject] = useState<string | null>(null)

  const { projects, isLoading, error, refetch } = useHostProjects(hostId)

  const {
    form,
    handleSessionNameNext,
    handleInitialMessageNext,
    handlePermissionsNext,
    resetForm,
    startSession,
    isSubmitting,
    error: submitError,
  } = useStartSessionForm({ setStep })

  const handleClose = useCallback(() => {
    setStep('project_select')
    setSelectedProject(null)
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleProjectSelect = useCallback((workDirectory: string) => {
    setSelectedProject(workDirectory)
    setStep('session_name')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!selectedProject) return
    const initialMessage = form.initialMessage.trim() || null
    const success = await startSession(
      selectedProject,
      form.sessionName.trim(),
      initialMessage,
      form.dangerouslySkipPermissions,
      userHostDetails,
    )
    if (success) {
      handleClose()
    }
  }, [form, selectedProject, userHostDetails, startSession, handleClose])

  return (
    <WizardModal open onClose={handleClose} ariaLabel="Start new session">
      {step === 'project_select' && (
        <ProjectSelectStep
          hostname={hostname}
          projects={projects}
          isLoading={isLoading}
          error={error}
          onSelect={handleProjectSelect}
          onCancel={handleClose}
          onRetry={refetch}
        />
      )}
      {step === 'session_name' && selectedProject !== null && (
        <SessionNameStep
          initialValue={form.sessionName}
          projectKey={selectedProject}
          onNext={handleSessionNameNext}
          onCancel={handleClose}
          onBack={() => setStep('project_select')}
        />
      )}
      {step === 'initial_message' && (
        <InitialMessageStep
          initialValue={form.initialMessage}
          onNext={handleInitialMessageNext}
          onBack={() => setStep('session_name')}
        />
      )}
      {step === 'permissions' && (
        <PermissionsModeStep
          initialValue={form.dangerouslySkipPermissions}
          onNext={handlePermissionsNext}
          onBack={() => setStep('initial_message')}
        />
      )}
      {step === 'review' && selectedProject !== null && (
        <ReviewStep
          form={form}
          projectKey={selectedProject}
          isSubmitting={isSubmitting}
          error={submitError}
          onSubmit={handleSubmit}
          onBack={() => setStep('permissions')}
        />
      )}
    </WizardModal>
  )
}
